import { ServiceType } from '../types'

export interface SEOMetadata {
  title: string
  description: string
  keywords: string[]
  canonicalUrl: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  structuredData?: Record<string, any>
}

export interface ServiceSEOConfig {
  title: string
  description: string
  keywords: string[]
  ogImage: string
  structuredData: Record<string, any>
}

// Service-specific SEO configurations
export const serviceSEOConfigs: Partial<Record<ServiceType, ServiceSEOConfig>> = {
  [ServiceType.HR]: {
    title: 'HR Templates & Recruitment Solutions | Mernaki',
    description: 'Professional recruitment templates and branded hiring pages. Get your custom HR solution from €1,000. Streamline your hiring process with Mernaki.',
    keywords: [
      'hr templates',
      'recruitment solutions',
      'hiring pages',
      'branded recruitment',
      'job posting templates',
      'hr tools',
      'recruitment branding',
      'hiring process'
    ],
    ogImage: '/images/og/hr-service.jpg',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'HR Templates & Recruitment Solutions',
      'description': 'Professional recruitment templates and branded hiring pages for businesses',
      'provider': {
        '@type': 'Organization',
        'name': 'Mernaki',
        'url': 'https://mernaki.de'
      },
      'serviceType': 'Human Resources',
      'areaServed': 'Europe',
      'offers': {
        '@type': 'Offer',
        'price': '1000',
        'priceCurrency': 'EUR',
        'description': 'Custom branded hiring page solution'
      }
    }
  },
  [ServiceType.COMPLIANCE]: {
    title: 'EU Compliance & Recall Insurance | Mernaki',
    description: 'Protect your business from EU recalls and compliance issues. Expert legal services and insurance solutions for European market compliance.',
    keywords: [
      'eu compliance',
      'recall insurance',
      'legal services',
      'european regulations',
      'business protection',
      'compliance consulting',
      'regulatory compliance',
      'eu market entry'
    ],
    ogImage: '/images/og/compliance-service.jpg',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'EU Compliance & Recall Insurance',
      'description': 'Legal services and insurance solutions for European market compliance',
      'provider': {
        '@type': 'Organization',
        'name': 'Mernaki',
        'url': 'https://mernaki.de'
      },
      'serviceType': 'Legal Services',
      'areaServed': 'Europe'
    }
  },
  [ServiceType.TRADE]: {
    title: 'Rare-Earth Export Permits & Trade Solutions | Mernaki',
    description: 'Secure verified rare-earth export permits and international trade solutions. Expert guidance for complex trade regulations and documentation.',
    keywords: [
      'rare earth permits',
      'export permits',
      'trade solutions',
      'international trade',
      'export documentation',
      'trade compliance',
      'rare earth elements',
      'export licensing'
    ],
    ogImage: '/images/og/trade-service.jpg',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Rare-Earth Export Permits & Trade Solutions',
      'description': 'Expert guidance for rare-earth export permits and international trade compliance',
      'provider': {
        '@type': 'Organization',
        'name': 'Mernaki',
        'url': 'https://mernaki.de'
      },
      'serviceType': 'Trade Services',
      'areaServed': 'Worldwide'
    }
  },
  [ServiceType.INTELLIGENCE]: {
    title: 'AI-Powered Job Intelligence & Lead Discovery | Mernaki',
    description: 'Discover hiring companies instantly with AI-powered job intelligence. Advanced lead discovery and market insights for recruitment professionals.',
    keywords: [
      'job intelligence',
      'lead discovery',
      'ai recruitment',
      'hiring companies',
      'recruitment leads',
      'job market insights',
      'recruitment intelligence',
      'hiring trends'
    ],
    ogImage: '/images/og/intelligence-service.jpg',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'AI-Powered Job Intelligence & Lead Discovery',
      'description': 'Advanced AI-powered lead discovery and market insights for recruitment professionals',
      'provider': {
        '@type': 'Organization',
        'name': 'Mernaki',
        'url': 'https://mernaki.de'
      },
      'serviceType': 'Business Intelligence',
      'areaServed': 'Europe'
    }
  }
}

// Main landing page SEO configuration
export const mainPageSEO: ServiceSEOConfig = {
  title: 'Mernaki - Unified Business Platform | HR, Compliance, Trade & Intelligence',
  description: 'Complete business solutions platform offering HR templates, EU compliance services, trade permits, and AI-powered intelligence. Your one-stop solution for business growth.',
  keywords: [
    'business platform',
    'hr solutions',
    'compliance services',
    'trade permits',
    'business intelligence',
    'mernaki',
    'european business',
    'b2b services'
  ],
  ogImage: '/images/og/main-platform.jpg',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Mernaki',
    'url': 'https://mernaki.de',
    'description': 'Unified business platform offering HR, compliance, trade, and intelligence solutions',
    'logo': 'https://mernaki.de/images/logo.png',
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'areaServed': 'Europe',
      'availableLanguage': ['English', 'German']
    },
    'sameAs': [
      'https://linkedin.com/company/mernaki',
      'https://twitter.com/mernaki'
    ],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Mernaki Services',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'HR Templates & Recruitment Solutions'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'EU Compliance & Recall Insurance'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Rare-Earth Export Permits & Trade Solutions'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'AI-Powered Job Intelligence & Lead Discovery'
          }
        }
      ]
    }
  }
}

// Generate SEO metadata for a specific service or main page
export const generateSEOMetadata = (
  serviceType?: ServiceType,
  customTitle?: string,
  customDescription?: string
): SEOMetadata => {
  const config = serviceType ? (serviceSEOConfigs[serviceType] || mainPageSEO) : mainPageSEO
  const baseUrl = 'https://mernaki.de'
  const path = serviceType ? `/${serviceType}` : ''
  
  return {
    title: customTitle || config.title,
    description: customDescription || config.description,
    keywords: config.keywords,
    canonicalUrl: `${baseUrl}${path}`,
    ogTitle: customTitle || config.title,
    ogDescription: customDescription || config.description,
    ogImage: `${baseUrl}${config.ogImage}`,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: customTitle || config.title,
    twitterDescription: customDescription || config.description,
    twitterImage: `${baseUrl}${config.ogImage}`,
    structuredData: config.structuredData
  }
}

// Update document head with SEO metadata
export const updateDocumentHead = (metadata: SEOMetadata): void => {
  // Update title
  document.title = metadata.title
  
  // Update or create meta tags
  const updateMetaTag = (name: string, content: string, property?: boolean) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
    let meta = document.querySelector(selector) as HTMLMetaElement
    
    if (!meta) {
      meta = document.createElement('meta')
      if (property) {
        meta.setAttribute('property', name)
      } else {
        meta.setAttribute('name', name)
      }
      document.head.appendChild(meta)
    }
    
    meta.setAttribute('content', content)
  }
  
  // Basic meta tags
  updateMetaTag('description', metadata.description)
  updateMetaTag('keywords', metadata.keywords.join(', '))
  
  // Open Graph tags
  updateMetaTag('og:title', metadata.ogTitle || metadata.title, true)
  updateMetaTag('og:description', metadata.ogDescription || metadata.description, true)
  updateMetaTag('og:image', metadata.ogImage || '', true)
  updateMetaTag('og:type', metadata.ogType || 'website', true)
  updateMetaTag('og:url', metadata.canonicalUrl, true)
  
  // Twitter Card tags
  updateMetaTag('twitter:card', metadata.twitterCard || 'summary_large_image')
  updateMetaTag('twitter:title', metadata.twitterTitle || metadata.title)
  updateMetaTag('twitter:description', metadata.twitterDescription || metadata.description)
  updateMetaTag('twitter:image', metadata.twitterImage || metadata.ogImage || '')
  
  // Canonical URL
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', metadata.canonicalUrl)
  
  // Structured data
  if (metadata.structuredData) {
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]')
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(metadata.structuredData)
  }
}

// Generate sitemap data
export interface SitemapEntry {
  url: string
  lastModified: string
  changeFrequency: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
  priority: number
}

export const generateSitemapData = (): SitemapEntry[] => {
  const baseUrl = 'https://mernaki.de'
  const currentDate = new Date().toISOString()
  
  const entries: SitemapEntry[] = [
    {
      url: baseUrl,
      lastModified: currentDate,
      changeFrequency: 'weekly',
      priority: 1.0
    }
  ]
  
  // Add service pages
  Object.values(ServiceType).forEach(serviceType => {
    entries.push({
      url: `${baseUrl}/${serviceType}`,
      lastModified: currentDate,
      changeFrequency: 'weekly',
      priority: 0.8
    })
  })
  
  return entries
}

// Generate XML sitemap
export const generateSitemapXML = (entries: SitemapEntry[]): string => {
  const xmlEntries = entries.map(entry => `
  <url>
    <loc>${entry.url}</loc>
    <lastmod>${entry.lastModified}</lastmod>
    <changefreq>${entry.changeFrequency}</changefreq>
    <priority>${entry.priority}</priority>
  </url>`).join('')
  
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${xmlEntries}
</urlset>`
}